import { navigate } from 'gatsby'
import { useEffect, useRef, useState } from 'react'
import { UserEventType, VALID_STATES } from '@flock/utils'
// @ts-ignore
import TimeMe from 'timeme.js'
import { PHONE_URL, PRETTY_PHONE_NUMBER } from '@flock/shared-ui'
import { Core_LeadStage } from '@flock/flock-gql-server/src/__generated__/graphql'

import { startCase } from 'lodash'
import { debounce } from '@mui/material'
import * as Sentry from '@sentry/gatsby'
import zipToTz from 'zip-to-tz'
import {
  HOMEPAGE_PATH,
  HOW_IT_WORKS_PATH,
  FAQ_PATH,
  BLOG_PATH,
  ABOUT_US_PATH,
  INVESTOR_PATH,
  PARTNER_PATH,
  AGENTS_PATH,
} from '@flock/shared-ui/src/routeConstants'
import {
  AGENTS_SALES_OPERATOR,
  ANDREESSEN_ARTICLE,
  BUSINESS_INSIDER_URL,
  CALENDLY_AGENTS_URL,
  CALENDLY_SCHEDULE_URL,
  DEFAULT_SALES_OPERATOR,
  DENVER_POST_ARTICLE_URL,
  JACOB_OPERATOR_UUID,
  JEREMY_OPERATOR_UUID,
  JACK_OPERATOR_UUID,
  TECH_CRUNCH_ARTICLE,
  ARI_OPERATOR_UUID,
  KYLE_OPERATOR_UUID,
  JENNETTE_OPERATOR_UUID,
  DEFAULT_SALES_SLACK_ID,
  AGENT_SALES_SLACK_ID,
  CHAD_OPERATOR_UUID,
  ELLIOT_OPERATOR_UUID,
} from '../constants'

import { ImageConfig } from './types'
import PrimaryImg from '../images/primary-venture.webp'
import SusaImg from '../images/susa-venture.webp'
import DenverPost from '../images/denver-post.webp'
import BusinessInsider from '../images/business-insider.webp'
import TechCrunch from '../images/tech-crunch.webp'
import Andreessen from '../images/andreessen.webp'
import RedwoodTrust from '../images/redwood-trust.webp'

import BoxGroup from '../images/box-group.webp'
import HumanCapital from '../images/human-capital.webp'
import Sharpe from '../images/1sharpe.webp'
import { track } from '../utils/analytics'

// ADD NEW SFRD HERE
const get45MinuteCalendlyForUUID = (uuid: string) => {
  switch (uuid) {
    case JEREMY_OPERATOR_UUID:
      return 'https://calendly.com/jeremy-flockhomes/45-minute-meeting'
    case JACK_OPERATOR_UUID:
      return 'https://calendly.com/jack-flockhomes/45-minute-meeting'
    case CHAD_OPERATOR_UUID:
      return 'https://calendly.com/chad_pimentel-flockhomes/45-minute-meeting'
    case ELLIOT_OPERATOR_UUID:
      return 'https://calendly.com/elliot-flockhomes/30min' // Not sure why the slug is '30min' but confirmed this is the correct link
    case KYLE_OPERATOR_UUID:
      return 'https://calendly.com/kyle-flockhomes/45-minute-meeting'
    case JENNETTE_OPERATOR_UUID:
      return 'https://calendly.com/jennette-flockhomes/45-minute-meeting'
    default:
      return ''
  }
}

export const getSalesAssignee = (operator: any, isAgent: boolean) => {
  if (isAgent) {
    return {
      calendlyLink: CALENDLY_AGENTS_URL,
      salesAssigneeName: AGENTS_SALES_OPERATOR,
      slackId: AGENT_SALES_SLACK_ID,
    }
  }
  // ADD NEW SFRD HERE
  if (
    operator?.uuid !== JACOB_OPERATOR_UUID &&
    operator?.uuid !== ARI_OPERATOR_UUID &&
    operator?.uuid !== KYLE_OPERATOR_UUID &&
    operator?.uuid !== JEREMY_OPERATOR_UUID &&
    operator?.uuid !== JACK_OPERATOR_UUID &&
    operator?.uuid !== JENNETTE_OPERATOR_UUID &&
    operator?.uuid !== CHAD_OPERATOR_UUID &&
    operator?.uuid !== ELLIOT_OPERATOR_UUID
  ) {
    return {
      calendlyLink: CALENDLY_SCHEDULE_URL,
      salesAssigneeName: DEFAULT_SALES_OPERATOR,
      slackId: DEFAULT_SALES_SLACK_ID,
      cgaSlackId: DEFAULT_SALES_SLACK_ID,
    }
  }
  return {
    calendlyLink:
      get45MinuteCalendlyForUUID(operator.uuid) || operator.calendlyLink,
    salesAssigneeName: operator.fullName,
    slackId: operator.slackId,
    cgaSlackId: DEFAULT_SALES_SLACK_ID,
  }
}

// Utility hook that takes an array of ids and returns the
// one that is currently on screen
export const useInView = (ids: string[], isLeadHubEnabled: boolean = false) => {
  const [currentSection, setCurrentSection] = useState(ids[0])
  const [hasInitialScroll, setHasInitialScroll] = useState(false)

  // Handle initial URL hash scroll
  useEffect(() => {
    if (!hasInitialScroll && ids.length > 0 && isLeadHubEnabled) {
      const hash = window.location.hash.slice(1)
      if (hash && ids.includes(hash)) {
        const element = document.getElementById(hash)
        if (element) {
          setTimeout(() => {
            // Calculate position to scroll 250px above the element
            const elementRect = element.getBoundingClientRect()
            const scrollTop =
              window.pageYOffset || document.documentElement.scrollTop
            const elementTop = elementRect.top + scrollTop

            // Calculate the scroll position to be 250px above the element
            const targetScrollTop = elementTop - 250

            window.scrollTo({
              top: targetScrollTop,
              behavior: 'smooth',
            })
          }, 100)
        }
      }
      setHasInitialScroll(true)
    }
  }, [ids, hasInitialScroll, isLeadHubEnabled])

  // Update URL hash only after initial scroll is handled
  useEffect(() => {
    if (
      hasInitialScroll &&
      currentSection &&
      typeof window !== 'undefined' &&
      isLeadHubEnabled
    ) {
      const url = new URL(window.location.href)
      url.hash = `#${currentSection}`
      window.history.replaceState(null, '', url.toString())
      track('section-view', {
        section: currentSection,
      })
    }
  }, [currentSection, hasInitialScroll, isLeadHubEnabled])

  const updateSections = () => {
    if (isLeadHubEnabled && !hasInitialScroll) return // Don't update until initial scroll is handled

    // If at the very top of the page, set to the first section
    if (window.pageYOffset === 0) {
      setCurrentSection(ids[0])
      return
    }

    let current = ids[0]
    ids.forEach((id) => {
      const element = document.getElementById(id)
      const sectionTop = element?.offsetTop || Number.MAX_SAFE_INTEGER
      // eslint-disable-next-line no-restricted-globals
      if (pageYOffset >= sectionTop - 250) {
        current = id
      }
    })
    setCurrentSection(current)
  }

  const debouncedUpdateSections = debounce(updateSections, 20)

  useEffect(() => {
    window.addEventListener('scroll', () => {
      debouncedUpdateSections()
    })
  }, [debouncedUpdateSections])

  return currentSection
}

type UseCalendlyProps = {
  utmMedium: string
  scheduleUrl: string
}

export const useCalendly = (props: UseCalendlyProps) => {
  const { utmMedium, scheduleUrl } = props
  const [isOpen, setOpen] = useState(false)
  const [element, setElement] = useState<HTMLElement | null>()
  useEffect(() => {
    setElement(document.getElementById('calendlyPopupRoot'))
  }, [])

  return {
    rootElem: element,
    scheduleUrl,
    utmMedium,
    isOpen,
    onClose: () => setOpen(false),
    onOpenCalendly: () => setOpen(true),
  }
}

export const sleep = (ms: number) => {
  const start = new Date().getTime()
  const expire = start + ms
  // eslint-disable-next-line no-empty
  while (new Date().getTime() < expire) {}
}

export const useRecordPageDuration = () => {
  useEffect(() => {
    const isBrowser = typeof window !== 'undefined'

    const isWindow = isBrowser && window

    if (isWindow) {
      window.onbeforeunload = () => {
        track('final-page-exit', {
          timeOnPage: TimeMe.getTimeOnCurrentPageInSeconds(),
          pageViewed: TimeMe.currentPageName,
          finalPage: 'exit',
          actionType: UserEventType.PAGE_DURATION,
        })

        sleep(300)

        return undefined
      }
      return () => {
        window.onbeforeunload = null
      }
    }
    return () => {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return null
}

export const useScrollDepthTracker = () => {
  const maxScrollDepth = useRef(0)

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY
      const windowHeight = window.innerHeight
      const fullHeight = document.documentElement.scrollHeight

      const currentDepth = Math.round(
        ((scrollTop + windowHeight) / fullHeight) * 100
      )

      const thresholds = [25, 50, 75, 100]
      thresholds.forEach((threshold) => {
        if (currentDepth >= threshold && threshold > maxScrollDepth.current) {
          maxScrollDepth.current = threshold

          // Send tracking event
          track('scroll-depth', {
            actionType: UserEventType.PAGE_DEPTH,
            depth: threshold,
          })
        }
      })
    }

    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])
}

export const scrollIntoView = (id: string) => {
  const el = document.getElementById(id)
  const yOffset = -32
  const y = el?.getBoundingClientRect().top! + window.pageYOffset + yOffset

  window.scrollTo({
    behavior: 'smooth',
    top: y,
  })
}

// TODO: Once we switch all headers to the new design,
// we can put these in the Header component
export const onLogoClick = () => {
  navigate(HOMEPAGE_PATH)
}
export const landingHeaderConfigs = [
  {
    text: 'Learn',
    href: '#',
    onClick: () => {},
    submenuOptions: [
      {
        text: 'How it works',
        mobileText: 'How it works',
        href: HOW_IT_WORKS_PATH,
        onClick: () => {
          navigate(HOW_IT_WORKS_PATH)
        },
      },
      {
        text: 'FAQ',
        mobileText: 'FAQ',
        href: FAQ_PATH,
        onClick: () => {
          navigate(FAQ_PATH)
        },
      },
      {
        text: 'Blog',
        mobileText: 'Blog',
        href: BLOG_PATH,
        onClick: () => {
          navigate(BLOG_PATH)
        },
      },
    ],
  },
  {
    text: 'About us',
    href: '#',
    onClick: () => {
      navigate(ABOUT_US_PATH)
    },
  },
  {
    text: 'Agents',
    href: AGENTS_PATH,
    onClick: () => {
      navigate(AGENTS_PATH)
    },
  },
  {
    text: 'Sign in',
    href: '#',
    onClick: () => {},
    submenuOptions: [
      {
        text: 'Investor',
        mobileText: 'Investor Sign In',
        href: INVESTOR_PATH as string,
      },
      {
        text: 'Partner Agent',
        mobileText: 'Partner Agent Sign In',
        href: PARTNER_PATH as string,
      },
    ],
  },
  {
    text: PRETTY_PHONE_NUMBER,
    onClick: () => {
      window.location.href = PHONE_URL
    },
  },
]

export const agentHeaderConfigs = [
  {
    text: 'Learn',
    href: '#',
    onClick: () => {},
    submenuOptions: [
      {
        text: 'How it works',
        mobileText: 'How it works',
        href: HOW_IT_WORKS_PATH,
        onClick: () => {
          navigate(HOW_IT_WORKS_PATH)
        },
      },
      {
        text: 'FAQ',
        mobileText: 'FAQ',
        href: FAQ_PATH,
        onClick: () => {
          navigate(FAQ_PATH)
        },
      },
      {
        text: 'Blog',
        mobileText: 'Blog',
        href: BLOG_PATH,
        onClick: () => {
          navigate(BLOG_PATH)
        },
      },
    ],
  },
  {
    text: 'About us',
    href: ABOUT_US_PATH,
    onClick: () => {
      navigate(ABOUT_US_PATH)
    },
  },
  {
    text: 'Agents',
    href: AGENTS_PATH,
    onClick: () => {
      navigate(AGENTS_PATH)
    },
  },
  {
    text: 'Sign in',
    href: '#',
    onClick: () => {},
    submenuOptions: [
      {
        text: 'Investor',
        mobileText: 'Investor Sign In',
        href: INVESTOR_PATH as string,
      },
      {
        text: 'Partner Agent',
        mobileText: 'Partner Agent Sign In',
        href: PARTNER_PATH as string,
      },
    ],
  },
  {
    text: PRETTY_PHONE_NUMBER,
    onClick: () => {
      window.location.href = PHONE_URL
    },
  },
]

export const agentClientHeaderConfigs = [
  {
    text: 'How it works',
    onClick: () => {
      navigate(HOW_IT_WORKS_PATH)
    },
  },
  {
    text: 'About us',
    onClick: () => {
      navigate(ABOUT_US_PATH)
    },
  },
  {
    text: 'FAQ',
    onClick: () => {
      navigate(FAQ_PATH)
    },
  },
  {
    text: 'For Agents',
    onClick: () => {
      navigate(AGENTS_PATH)
    },
  },
  {
    text: 'Sign in',
    onClick: () => {
      window.location.href = INVESTOR_PATH
    },
  },
]

export const decomposeSlackUrl = (slackUrl: string) => {
  const splitUrl = slackUrl.split('/')
  const channel = splitUrl[4]
  const threadTimestamp = splitUrl[5].split('p')[1]

  return {
    channel,
    threadTimestamp,
  }
}

export const isValidMarket = (state: string) => VALID_STATES.includes(state)

// Property value to property tax based on https://www.rocketmortgage.com/learn/property-taxes-by-state
export const stateToPropertyTax: { [key: string]: number } = {
  AL: 0.0041,
  AK: 0.0119,
  AZ: 0.0066,
  AR: 0.0062,
  CA: 0.0076,
  CO: 0.0051,
  CT: 0.0214,
  DE: 0.0057,
  DC: 0.0056,
  FL: 0.0089,
  GA: 0.0092,
  HI: 0.0028,
  ID: 0.0069,
  IL: 0.0227,
  IN: 0.0085,
  IA: 0.0157,
  KS: 0.0141,
  KY: 0.0086,
  LA: 0.0055,
  ME: 0.0136,
  MD: 0.0109,
  MA: 0.0123,
  MI: 0.0154,
  MN: 0.0112,
  MS: 0.0081,
  MO: 0.0097,
  MT: 0.0084,
  NE: 0.0173,
  NV: 0.006,
  NH: 0.0218,
  NJ: 0.0249,
  NM: 0.008,
  NY: 0.0172,
  NC: 0.0084,
  ND: 0.0098,
  OH: 0.0156,
  OK: 0.009,
  OR: 0.0097,
  PA: 0.0158,
  RI: 0.0163,
  SC: 0.0057,
  SD: 0.0131,
  TN: 0.0071,
  TX: 0.018,
  UT: 0.0063,
  VT: 0.019,
  VA: 0.0082,
  WA: 0.0098,
  WV: 0.0058,
  WI: 0.0185,
  WY: 0.0061,
}

export const articleConfigs: ImageConfig[] = [
  {
    href: BUSINESS_INSIDER_URL,
    img: BusinessInsider,
    alt: 'Business Insider',
    quote: 'Steady income without the hassles of property ownership',
  },
  {
    href: DENVER_POST_ARTICLE_URL,
    img: DenverPost,
    alt: 'Denver Post',
    quote: 'Ideal for accidental landlords',
  },
  {
    href: TECH_CRUNCH_ARTICLE,
    img: TechCrunch,
    alt: 'Tech Crunch',
    quote: 'Fair and transparent for every owner',
  },
]

export const backerConfigs1: ImageConfig[] = [
  {
    href: ANDREESSEN_ARTICLE,
    img: Andreessen,
    alt: 'Andreessen Horowitz',
  },
  {
    href: 'https://human.capital/',
    img: HumanCapital,
    alt: 'Human Capital',
  },
  {
    href: 'https://1sharpe.com/',
    img: Sharpe,
    alt: '1Sharpe',
  },
]

export const backerConfigs2: ImageConfig[] = [
  {
    href: 'https://ir.redwoodtrust.com/news/news-details/2021/Redwood-Trust-Announces-Investment-In-Pioneering-Rental-Ownership-Platform-Flock/default.aspx',
    img: RedwoodTrust,
    alt: 'Redwood Trust',
  },
  {
    href: 'https://medium.com/thoughts-from-primary-venture-partners/why-we-backed-flock-homes-and-how-its-unlocking-value-for-single-family-rental-owners-d3580d7ec336',
    img: PrimaryImg,
    alt: 'Primary Ventures',
  },
  {
    href: 'https://medium.com/susa-ventures/our-investment-in-flock-homes-92728774244a',
    img: SusaImg,
    alt: 'Susa Ventures',
  },
  {
    href: 'https://www.boxgroup.com/',
    img: BoxGroup,
    alt: 'Box Group',
  },
]

export const abbreviationToState: { [key: string]: string } = {
  AL: 'Alabama',
  AK: 'Alaska',
  AS: 'American Samoa',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District Of Columbia',
  FM: 'Federated States Of Micronesia',
  FL: 'Florida',
  GA: 'Georgia',
  GU: 'Guam',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MH: 'Marshall Islands',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  MP: 'Northern Mariana Islands',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PW: 'Palau',
  PA: 'Pennsylvania',
  PR: 'Puerto Rico',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VI: 'Virgin Islands',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
}

export const isUuid = (str: string) => {
  const regex = new RegExp(
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  )
  return regex.test(str)
}

export const normalizeName = (str: string) => {
  if (!str) {
    return str
  }

  let resStr = str.trim().replace(/\s+/g, ' ')

  // if it's only first name, add unknown as last name
  resStr = str.match(/^[A-Za-z]*$/) ? `${str} Unknown` : str

  return startCase(resStr)
}

export const getTimeForZip = (date?: Date, zipcode?: string) => {
  let displayedDate = ''
  let displayedTime = ''
  if (date) {
    if (zipcode) {
      let leadTimeZone = ''
      try {
        leadTimeZone = zipToTz(zipcode) || ''
      } catch (e) {
        Sentry.captureException(
          new Error('Could not find timezone for zipcode'),
          {
            extra: {
              zipcode,
            },
          }
        )
      }

      displayedDate = date.toLocaleDateString(undefined, {
        timeZone: leadTimeZone,
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
      displayedTime = date.toLocaleTimeString(undefined, {
        timeZoneName: 'short',
        timeZone: leadTimeZone,
        hour: '2-digit',
        minute: '2-digit',
      })
    }
  }

  return displayedDate ? `${displayedDate} at ${displayedTime}` : ''
}

export const hideExpirationLeadStages = [
  Core_LeadStage.LeadStageContributionSigned,
  Core_LeadStage.LeadStageAgreementSent,
  Core_LeadStage.LeadStageConsensus,
  Core_LeadStage.LeadStageAlignment,
]

/**
 * Gets the last business day of the month on or before the 15th of the month before the closeDate.
 * This is used to determine the signing date for exchange processes.
 * @param closeDate - The target close date
 * @returns The signing date as a Date object
 */
export const getSigningDate = (
  signingDate: Date | null,
  closeDate: Date
): Date => {
  const signingDateObj = new Date(signingDate || '')
  if (
    signingDateObj &&
    signingDateObj.toISOString() !== '0001-01-01T00:00:00.000Z'
  ) {
    // Get the month before the close date
    signingDateObj.setDate(signingDateObj.getDate() + 1) // adjust for UTC
    return signingDateObj
  }
  // Get the month before the close date
  const monthBeforeClose = new Date(closeDate)
  monthBeforeClose.setDate(monthBeforeClose.getDate() + 1) // adjust for UTC
  monthBeforeClose.setMonth(monthBeforeClose.getMonth() - 1)

  // Set to the 15th of that month
  const fifteenthOfMonth = new Date(
    monthBeforeClose.getFullYear(),
    monthBeforeClose.getMonth(),
    15
  )

  // Find the last business day on or before the 15th
  const generatedSigningDate = new Date(fifteenthOfMonth)

  // Keep going backwards until we find a business day (Monday = 1, Sunday = 0)
  while (
    generatedSigningDate.getDay() === 0 ||
    generatedSigningDate.getDay() === 6
  ) {
    generatedSigningDate.setDate(generatedSigningDate.getDate() - 1)
  }

  return generatedSigningDate
}

/**
 * Gets the ordinal suffix for a given day number.
 * @param dayString - The day number as a string
 * @returns The ordinal suffix as a string
 */
export const getOrdinalSuffix = (dayString: string): string => {
  const dayNum = parseInt(dayString, 10)
  if (dayNum >= 11 && dayNum <= 13) return 'th'
  switch (dayNum % 10) {
    case 1:
      return 'st'
    case 2:
      return 'nd'
    case 3:
      return 'rd'
    default:
      return 'th'
  }
}
